import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';

interface ThreeExplosionProps {
    width?: string | number;
    height?: string | number;
    showCongratulatoryEffect?: boolean;
    congratulatoryMiner?: string | null;
}

const ThreeExplosion: React.FC<ThreeExplosionProps> = ({ width = '100%', height = '100%', showCongratulatoryEffect = false, congratulatoryMiner }) => {
    const mountRef = useRef<HTMLDivElement | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const fragmentMeshes = useRef<THREE.Mesh[]>([]); // Store exploded fragments
    const textMeshRef = useRef<THREE.Mesh | null>(null); // Reference to text mesh

    useEffect(() => {
        if (!mountRef.current) return;

        // Declare explosionTriggered at the top of the useEffect
        let explosionTriggered = false;

        // Create scene and camera
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.set(0, 0, 40);
        camera.lookAt(0, 0, 0);

        // Create renderer and center it
        const renderer = new THREE.WebGLRenderer({ alpha: true });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.domElement.style.position = 'absolute';
        renderer.domElement.style.top = '50%';
        renderer.domElement.style.left = '50%';
        renderer.domElement.style.transform = 'translate(-50%, -50%)';
        mountRef.current.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Define colors for color-changing effect
        const colors = [new THREE.Color(0xff00ff), new THREE.Color(0x00ff00), new THREE.Color(0x0000ff), new THREE.Color(0xffa500)];
        let colorIndex = 0;
        let transitionProgress = 0;

        // Create the sphere with initial color
        const sphereGeometry = new THREE.SphereGeometry(10, 32, 32);
        const wireframeMaterial = new THREE.MeshBasicMaterial({ color: colors[colorIndex], wireframe: true });
        const sphere = new THREE.Mesh(sphereGeometry, wireframeMaterial);
        sphere.position.set(0, 0, 0);
        scene.add(sphere);

        // Group for the main cube and layered edges
        const cubeGroup = new THREE.Group();

        // Define the main cube geometry with the same color as outer layers
        const boxGeometry = new THREE.BoxGeometry(18, 18, 18);
        const boxEdges = new THREE.EdgesGeometry(boxGeometry);
        const outerColor = new THREE.Color("#7dd6d4");
        const mainCubeMaterial = new THREE.LineBasicMaterial({ color: outerColor, opacity: 0.5, transparent: true });
        const mainCube = new THREE.LineSegments(boxEdges, mainCubeMaterial);
        cubeGroup.add(mainCube);

        // Add multiple layers of boxes around the main cube for depth, with the same color on all layers
        const numLayers = 10;
        const layerSpacing = 1.5;

        for (let i = 1; i <= numLayers; i++) {
            const size = 18 + i * layerSpacing;
            const layerGeometry = new THREE.BoxGeometry(size, size, size);
            const layerEdges = new THREE.EdgesGeometry(layerGeometry);

            const layerMaterial = new THREE.LineBasicMaterial({ color: outerColor, opacity: 0.2 + (i / numLayers) * 0.8, transparent: true });
            const layerLines = new THREE.LineSegments(layerEdges, layerMaterial);
            cubeGroup.add(layerLines);
        }

        // Add the cube group to the scene
        scene.add(cubeGroup);

        // Create fragments with a fixed color (7dd6d4) if showCongratulatoryEffect is true
        const createFragments = () => {
            fragmentMeshes.current = []; // Clear any existing fragments
            const fragmentGeometry = new THREE.SphereGeometry(1, 8, 8);
            const fragmentMaterial = new THREE.MeshBasicMaterial({ color: new THREE.Color("#7dd6d4"), wireframe: true });

            for (let i = 0; i < 50; i++) {
                const fragment = new THREE.Mesh(fragmentGeometry, fragmentMaterial);
                fragment.position.set(
                    (Math.random() - 0.5) * 20,
                    (Math.random() - 0.5) * 20,
                    (Math.random() - 0.5) * 20
                );
                scene.add(fragment);
                fragmentMeshes.current.push(fragment);
            }
        };

        // Congratulatory Text with gradient effect
  // Congratulatory Text with gradient effect
const createCongratulatoryText = () => {
    const fontLoader = new FontLoader();
    fontLoader.load('/fonts/helvetiker_bold.typeface.json', (font) => {
        const textGeometry = new TextGeometry(`Congratulations\n${congratulatoryMiner?.slice(0, 4)}...${congratulatoryMiner?.slice(-4)}`, {
            font,
            size: 1,
            height: 0.5,
            curveSegments: 12,
        });
        textGeometry.center();
        // Create a canvas texture to simulate gradient
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 64;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
            gradient.addColorStop(0, '#f7f6d6');
            gradient.addColorStop(0.3385, '#ccd0ff');
            gradient.addColorStop(0.6615, '#eca6d0');
            gradient.addColorStop(1, '#a8c8ff');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        const texture = new THREE.CanvasTexture(canvas);
        const textMaterial = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            depthTest: false // Disable depth testing for this material
        });
        const textMesh = new THREE.Mesh(textGeometry, textMaterial);
        textMesh.renderOrder = 1; // Render this on top
        textMesh.position.set(0, 0, 5); // Place the text closer to the camera
        scene.add(textMesh);
        textMeshRef.current = textMesh;
    });
};


        // Trigger congratulatory effect immediately if showCongratulatoryEffect is true
        if (showCongratulatoryEffect) {
            createFragments(); // Create fragments
            createCongratulatoryText(); // Create congratulatory text
            explosionTriggered = true;
            scene.remove(sphere); // Remove the original sphere to simulate an explosion
        }

        // Animation loop
        const animate = () => {
            if (!explosionTriggered) {
                sphere.rotation.x += 0.01;
                sphere.rotation.y += 0.01;

                // Smooth color transition for sphere
                const currentColor = colors[colorIndex];
                const nextColor = colors[(colorIndex + 1) % colors.length];
                wireframeMaterial.color.lerpColors(currentColor, nextColor, transitionProgress);

                transitionProgress += 0.002;
                if (transitionProgress >= 1) {
                    transitionProgress = 0;
                    colorIndex = (colorIndex + 1) % colors.length;
                }
            } else {
                fragmentMeshes.current.forEach(fragment => {
                    fragment.position.x += (fragment.position.x > 0 ? 0.1 : -0.1);
                    fragment.position.y += (fragment.position.y > 0 ? 0.1 : -0.1);
                    fragment.position.z += (fragment.position.z > 0 ? 0.1 : -0.1);
                });
            }

            cubeGroup.rotation.x += 0.005;
            cubeGroup.rotation.y += 0.005;

            if (textMeshRef.current) {
                textMeshRef.current.scale.x += 0.01;
                textMeshRef.current.scale.y += 0.01;
                textMeshRef.current.scale.z += 0.01;
            }

            renderer.render(scene, camera);
            requestAnimationFrame(animate);
        };
        animate();

        // Resize handling
        const handleResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (mountRef.current && rendererRef.current && rendererRef.current.domElement.parentNode === mountRef.current) {
                mountRef.current.removeChild(rendererRef.current.domElement);
            }
            rendererRef.current?.dispose();
            scene.clear();
        };
    }, [showCongratulatoryEffect, congratulatoryMiner]);

    return (
        <div style={{ position: 'relative', width, height, overflow: 'hidden' }}>
            <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
        </div>
    );
};

export default ThreeExplosion;
